import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@sbd-doug-frontend/app/environments/environment';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  map,
  switchMap,
  take, of,
} from 'rxjs';
import {
  Account, AccountAccountUserListResponse,
  AccountListItem,
  AccountListResponse, AccountLogoUploadResponse, AccountQuoteRequest, AccountQuoteRequestsResponse,
  AccountResponse,
  AccountUserListResponse,
  AccountUserLookups,
  AccountUserLookupsResponse,
  AccountUserPasswordObject,
  AccountUserResponse,
  AccountStatistics,
  AccountStatisticsResponse,
  AccountUserRoleItem, UploadLogoResult,
} from './accounts.types';
import { AccountUser } from '@sbd-doug-frontend/shared/lib/types/accounts.types';
import { Agent, AgentResponse } from '@sbd-doug-frontend/shared/lib/types/agents.types';
import {
  LoadedQuoteRequestsItem,
  LoadedQuoteRequestsResponse,
} from '@sbd-doug-frontend/app/modules/quotes/request/request.types';
import { AccountUserListItem } from '@sbd-doug-frontend/shared/lib/types/account.types';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  accountStatuses = signal<[]>([]);
  selectedAccountUser = signal<AccountUser>(null);
  selectedAccount = signal<Account>(null);
  private _account: BehaviorSubject<Account | null> = new BehaviorSubject(null);
  private _accountList: BehaviorSubject<AccountListItem[] | null> =
    new BehaviorSubject(null);
  private _totalAccounts: BehaviorSubject<number> = new BehaviorSubject(0);
  private _accountUser: BehaviorSubject<AccountUser | null> =
    new BehaviorSubject(null);
  private _accountUserList: BehaviorSubject<AccountUserListItem[] | null> =
    new BehaviorSubject(null);
  private _totalAccountUsers: BehaviorSubject<number> = new BehaviorSubject(0);
  private _accountUserLookups: BehaviorSubject<AccountUserLookups | null> =
    new BehaviorSubject(null);
  private _accountUserRoles: BehaviorSubject<AccountUserRoleItem[] | null> =
    new BehaviorSubject(null);

  accountListLoading = signal<boolean>(false);
  accountUserListLoading = signal<boolean>(false);
  accountQuoteRequests = signal<AccountQuoteRequest[]>([]);
  accountQuoteRequestsApiCallTriggered = signal<boolean>(false);
  accountQuoteRequestsTotal = signal<number>(0);
  accountQuoteRequestsLoading = signal<boolean>(false);

  clearRowSelection = signal<boolean>(false);
  currentLoadedQuoteRequest = signal<LoadedQuoteRequestsItem>(null);
  // Fired when account context changes and lists should reset UI state
  accountContextChanged$ = new Subject<void>();
  accountStatistics = signal<AccountStatistics>(null);
  accountStatisticsLoading = signal<boolean>(false);

  private _sessionStorage: Storage;
  private _storageKey = 'sharedServiceCache-system-account-user-ui-lookups';

  constructor(
    private _httpClient: HttpClient,
    private _cathyService: CathyService,
  ) {
  }

  get account$(): Observable<Account> {
    return this._account.asObservable();
  }

  resendAccountUserInvite(accountUserId: number): Observable<boolean> {
    return this._httpClient
      .get<AccountUserResponse>(
        `${environment.apiBaseUrl}/api/v4/accounts/user/${accountUserId}/send-ftl`
      )
      .pipe(
        map((response) => {
          if (!response || response.success === false) {
            const msg = response?.message ?? 'Unable to resend invite.';
            this._cathyService.error(msg);
            return false;
          }
          this._cathyService.success('Invite resent successfully.');
          return true;
        }),
        catchError((err) => {
          this._cathyService.error('Unable to resend invite.');
          return EMPTY;
        }),
      );
  }

  get accountUser$(): Observable<AccountUser> {
    return this._accountUser.asObservable();
  }

  get totalAccounts$(): Observable<number> {
    return this._totalAccounts.asObservable();
  }

  get totalAccountUsers$(): Observable<number> {
    return this._totalAccountUsers.asObservable();
  }

  get accountList$(): Observable<AccountListItem[]> {
    return this._accountList.asObservable();
  }

  get accountUserList$(): Observable<AccountUserListItem[]> {
    return this._accountUserList.asObservable();
  }

  get accountUserLookups$(): Observable<AccountUserLookups> {
    return this._accountUserLookups.asObservable();
  }

  get accountUserRoles$(): Observable<AccountUserRoleItem[]> {
    return this._accountUserRoles.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  //ACCOUNT METHODS

  getAccountsList(filters: any = {}): Observable<AccountListItem[]> {
    const params = filters || {};

    this.accountListLoading.set(true);
    return this._httpClient
      .get<AccountListResponse>(`${environment.apiBaseUrl}/api/v4/accounts`, {
        params: params,
      })
      .pipe(
        map((response) => {
          this._accountList.next(response.data.accounts.accounts);
          this._totalAccounts.next(response.data.accounts.total_accounts);
          this.accountListLoading.set(false);
          return response.data.accounts.accounts;
        }),
      );
  }

  getAccount(accountId: number): Observable<Account> {
    return this._httpClient
      .get<AccountResponse>(
        `${environment.apiBaseUrl}/api/v4/account/${accountId}`,
      )
      .pipe(
        map((response: AccountResponse) => {
          if (response.success === false) {
            this._account.next(null);
            this.selectedAccount.set(null);

            return null;
          } else {
            response.data.account.account.agency_default_commission = response.data.account.account.agency_default_commission * 100;
            this._account.next(response.data.account.account);
            this.selectedAccount.set(response.data.account.account);
            return response.data.account.account;
          }
        }),
        catchError((err, caught) => {
          return EMPTY;
        }),
      );
  }

  clearAccount() {
    this._account.next(null);
  }

  updateAccount(account: Account, sendApprovedEmail: boolean = false): Observable<AccountResponse> {
    return this.accountList$.pipe(
      take(1),
      switchMap((currentAccount) =>
        this._httpClient
          .put<AccountResponse>(
            `${environment.apiBaseUrl}/api/v4/account/${account.account_id}`,
            { ...account, send_approval_email_to_account_user: sendApprovedEmail },
          )
          .pipe(
            map((accountResponse) => {
              if (accountResponse.success == false) {
                return accountResponse;
              }

              if (currentAccount) {
                const index = currentAccount.findIndex(
                  (item) => item.account_id === account.account_id,
                );

                currentAccount[index] = accountResponse.data.account.account;
              }
              accountResponse.data.account.account.agency_default_commission = accountResponse.data.account.account.agency_default_commission * 100;
              this._accountList.next(currentAccount);
              this._account.next(accountResponse.data.account.account);
              return accountResponse;
            }),
          ),
      ),
    );
  }

  updateAccountFields(accountId: number, fields: { [key: string]: any }): Observable<AccountResponse> {
    return this.accountList$.pipe(
      take(1),
      switchMap((currentAccount) =>
        this._httpClient
          .patch<AccountResponse>(
            `${environment.apiBaseUrl}/api/v4/account/${accountId}`,
            fields,
          )
          .pipe(
            map((accountResponse) => {
              if (accountResponse.success == false) {
                return accountResponse;
              }

              if (currentAccount) {
                const index = currentAccount.findIndex(
                  (item) => item.account_id === accountId,
                );

                if (index !== -1) {
                  currentAccount[index] = accountResponse.data.account.account;
                }
              }
              accountResponse.data.account.account.agency_default_commission = accountResponse.data.account.account.agency_default_commission * 100;
              this._accountList.next(currentAccount);
              this._account.next(accountResponse.data.account.account);
              return accountResponse;
            }),
          ),
      ),
    );
  }

  create(account: Account, sendApprovedEmail: boolean = false): Observable<AccountResponse> {
    return this.accountList$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .post<AccountResponse>(
            `${environment.apiBaseUrl}/api/v4/account`,
            { ...account, send_approval_email_to_account_user: sendApprovedEmail },
          )
          .pipe(
            map((updatedAccount) => {
              if (updatedAccount.success == false) {
                // throw new Error(updatedAccount.message);
                return updatedAccount;
              } else {
                this._account.next(updatedAccount.data.account.account);
                return updatedAccount;
              }
            }),
          ),
      ),
    );
  }

  //ACCOUNT USER METHODS
  getAccountUsers(filters: any = {}): Observable<AccountUserListItem[]> {
    const params = filters || {};

    this.accountUserListLoading.set(true);
    return this._httpClient
      .get<AccountUserListResponse>(
        `${environment.apiBaseUrl}/api/v4/accounts/users`,
        {
          params: params,
        },
      )
      .pipe(
        map((response) => {
          this._accountUserList.next(response.data.account_users.account_users);
          this._totalAccountUsers.next(
            response.data.account_users.total_account_users,
          );
          this.accountUserListLoading.set(false);
          return response.data.account_users.account_users;
        }),
      );
  }

  loadAccountAccountUsers(filters: any = {}): Observable<AccountUserListItem[]> {
    const params = filters || {};

    this.accountUserListLoading.set(true);
    return this._httpClient
      .get<AccountAccountUserListResponse>(
        `${environment.apiBaseUrl}/api/v4/account/${this.selectedAccount().account_id}/account-users`,
        {
          params: params,
        },
      )
      .pipe(
        map((response) => {
          this._accountUserList.next(response.data.account.account_users);
          this._totalAccountUsers.next(
            response.data.account.total_account_users,
          );
          this.accountUserListLoading.set(false);
          return response.data.account.account_users;
        }),
      );
  }

  getAccountUser(accountUserId): Observable<AccountUser> {
    return this._httpClient
      .get<AccountUserResponse>(
        `${environment.apiBaseUrl}/api/v4/accounts/user/${accountUserId}`,
      )
      .pipe(
        map((response: AccountUserResponse) => {
          if (response.success === false) {
            this._accountUser.next(null);
            return null;
          } else {
            this._accountUser.next(response.data.account_user.account_user);
            this.selectedAccountUser.set(response.data.account_user.account_user);
            return response.data.account_user.account_user;
          }
        }),
        catchError((err, caught) => {
          return EMPTY;
        }),
      );
  }

  clearAccountUser() {
    this._accountUser.next(null);
  }

  changeAccountUserPassword(
    pass: AccountUserPasswordObject,
    accountUserId: number,
  ): Observable<boolean> {
    return this._accountUser.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .put<AccountUserResponse>(
            `${environment.apiBaseUrl}/api/v4/accounts/user/${accountUserId}/change-password`,
            pass,
          )
          .pipe(
            map((updatedAccountUser) => {
              if (updatedAccountUser.success === false) {
                throw new Error(updatedAccountUser.message);
              } else {
                // this._accountUser.next(
                //   updatedAccountUser.data.account_user.account_user
                // );
                // return updatedAccountUser.data.account_user.account_user;
                return true;
              }
            }),
          ),
      ),
    );
  }

  updateAccountUser(
    user: AccountUser,
    accountUserId: number,
  ): Observable<AccountUser> {
    return this.accountUserList$.pipe(
      take(1),
      switchMap((currentAccountUser) =>
        this._httpClient
          .put<AccountUserResponse>(
            `${environment.apiBaseUrl}/api/v4/accounts/user/${accountUserId}`,
            user,
          )
          .pipe(
            map((updatedAccountUser) => {
              if (updatedAccountUser.success == false) {
                this._cathyService.error(updatedAccountUser.message);
                throw new Error(updatedAccountUser.message);
              } else {
                this._accountUserList.next(currentAccountUser);
                this._accountUser.next(
                  updatedAccountUser.data.account_user.account_user,
                );
                return updatedAccountUser.data.account_user.account_user;
              }
            }),
          ),
      ),
    );
  }

  createAccountUser(user: AccountUser): Observable<AccountUserResponse> {
    return this.accountUserList$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .post<AccountUserResponse>(
            `${environment.apiBaseUrl}/api/v4/accounts/user`,
            user,
          )
          .pipe(
            map((updatedAccountUser) => {
              if (updatedAccountUser.success == false) {
                throw new Error(updatedAccountUser.message);
              } else {
                this._accountUser.next(
                  updatedAccountUser.data.account_user.account_user,
                );
                return updatedAccountUser;
              }
            }),
          ),
      ),
    );
  }

  // sendForgotPassword() {
  //   this._httpClient.get<AccountUserPasswordResponse>(
  //     `${environment.apiBaseUrl}/api/v4/accounts/user/1/forgot-password`,
  //     password
  //   );
  // }

  getAccountUsersUILookups(): Observable<AccountUserLookups> {
    return this._httpClient
      .get<AccountUserLookupsResponse>(
        `${environment.apiBaseUrl}/api/v4/accounts/users/ui-lookups`,
      )
      .pipe(
        map((response) => {
          if (
            !response ||
            response.success === false ||
            response.data === null ||
            response.data === undefined ||
            response.data === undefined ||
            response.data.ui_lookups === null
          ) {
            throw new Error(
              response.message ?? 'Unable to load account user UI lookups.',
            );
          }
          this._accountUserLookups.next(response.data.ui_lookups);
          this._accountUserRoles.next(response.data.ui_lookups.user_roles);
          //this.saveUILookupsCache(response.data.ui_lookups);
          return response.data.ui_lookups;
        }),
      );
  }

  // getUILookupsCache(): AccountUserLookups {
  //   const storageItem = this._sessionStorage.getItem(this._storageKey);
  //   return storageItem
  //     ? <AccountUserLookups>JSON.parse(storageItem)
  //     : undefined;
  // }

  // saveUILookupsCache(uiLookupTypes: AccountUserLookups) {
  //   const jsonData = JSON.stringify(uiLookupTypes);
  //   this._sessionStorage.setItem(this._storageKey, jsonData);
  // }
  checkAccountUserExistence(email: string): Observable<AccountUser> {
    return this.accountUserList$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .post<AccountUserResponse>(
            `${environment.apiBaseUrl}/api/v4/accounts/user/check`,
            { email },
          )
          .pipe(
            map((accountUserResponse) => {
              if (accountUserResponse.success == false) {
                this._cathyService.error(accountUserResponse.message);
                throw new Error(accountUserResponse.message);
              } else {
                return accountUserResponse.data.account_user.account_user;
              }
            }),
          ),
      ),
    );
  }


  getAgentById(agentId: number): Observable<Agent> {
    return this._httpClient
      .get<AgentResponse>(
        `${environment.apiBaseUrl}/api/v4/agent/${agentId}`,
      )
      .pipe(
        map((agentResponse) => {
          if (agentResponse.success == false) {
            this._cathyService.error(agentResponse.message);
            throw new Error(agentResponse.message);
          } else {
            return agentResponse.data.agent.agent;
          }
        }),
      );
  }

  getAccountQuoteRequests(account_id: number, filters: any = {}): Observable<AccountQuoteRequest[]> {
    const params = filters || {};

    this.accountQuoteRequestsLoading.set(true);
    return this._httpClient
      .get<AccountQuoteRequestsResponse>(`${environment.apiBaseUrl}/api/v4/account/${account_id}/quote-requests`, {
        params: params,
      })
      .pipe(
        map((response) => {
          this.accountQuoteRequests.set(response.data.account.quote_requests);
          this.accountQuoteRequestsTotal.set(response.data.account.total_quote_requests);
          this.accountQuoteRequestsLoading.set(false);
          return response.data.account.quote_requests;
        }));

  }


  loadQuoteRequest(quoteRequestId: number): Observable<LoadedQuoteRequestsItem> {
    return this._httpClient
      .get<LoadedQuoteRequestsResponse>(
        `${environment.apiBaseUrl}/api/v4/quote/load-request/${quoteRequestId}`,
      ).pipe(
        map((response) => {
          this.currentLoadedQuoteRequest.set(response.data.quote_request.quote_request);
          return response.data.quote_request.quote_request;
        }),
      );
  }

  loadAccountQuoteRequest(accountId: number, quoteRequestId: number): Observable<LoadedQuoteRequestsItem> {
    return this._httpClient
      .get<LoadedQuoteRequestsResponse>(
        `${environment.apiBaseUrl}/api/v4/quote/account/${accountId}/load-request/${quoteRequestId}`,
      ).pipe(
        map((response) => {
          this.currentLoadedQuoteRequest.set(response.data.quote_request.quote_request);
          return response.data.quote_request.quote_request;
        }),
      );
  }


  getAccountStatistics(accountId: number): Observable<AccountStatistics> {
    this.accountStatisticsLoading.set(true);
    return this._httpClient
      .get<AccountStatisticsResponse>(`${environment.apiBaseUrl}/api/v4/account/${accountId}/statistics`)
      .pipe(
        map((response) => {
          const raw = response.data.account_statistics.account_statistics;
          const toNumber = (v: string | number | null | undefined): number => {
            if (v === null || v === undefined) return 0;
            const n = typeof v === 'string' ? parseInt(v, 10) : v;
            return isNaN(n as number) ? 0 : (n as number);
          };
          const normalized: AccountStatistics = {
            customers: {
              totals: {
                total_customers: toNumber(raw?.customers?.totals?.total_customers),
              },
            },
            quote_requests: {
              totals: {
                total: toNumber(raw?.quote_requests?.totals?.total),
                new: toNumber(raw?.quote_requests?.totals?.new),
                renewal: toNumber(raw?.quote_requests?.totals?.renewal),
              },
              issued: {
                total: toNumber(raw?.quote_requests?.issued?.total),
                new: toNumber(raw?.quote_requests?.issued?.new),
                renewal: toNumber(raw?.quote_requests?.issued?.renewal),
              },
            },
          };
          this.accountStatistics.set(normalized);
          this.accountStatisticsLoading.set(false);
          return normalized;
        }),
      );
  }

  uploadLogos(accountId: number, formData: FormData): Observable<AccountLogoUploadResponse> {
    return this._httpClient.post<AccountLogoUploadResponse>
    (`${environment.apiBaseUrl}/api/v4/account/${accountId}/upload-logos`, formData)
      .pipe(
        map((response) => {
          if(!response?.success){
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          this._account.next(response.data.account.account);
          this.selectedAccount.set(response.data.account.account)
          return response;
        }),
      );
  }

  uploadSingleLogo(accountId: number, shape: string, mode: string, file: File): Observable<AccountResponse> {
    const formData = new FormData();
    formData.append('logo', file);

    return this._httpClient.post<AccountResponse>
    (`${environment.apiBaseUrl}/api/v4/account/${accountId}/logo/${shape}/${mode}`, formData)
      .pipe(
        map((response) => {
          if(!response?.success){
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          this._account.next(response.data.account.account);
          this.selectedAccount.set(response.data.account.account)
          return response;
        }),
      );
  }

  deleteLogo(accountId: number, shape: string, mode: string): Observable<AccountResponse> {
    return this._httpClient.delete<AccountResponse>
    (`${environment.apiBaseUrl}/api/v4/account/${accountId}/logo/${shape}/${mode}`)
      .pipe(
        map((response) => {
          if(!response?.success){
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          this._account.next(response.data.account.account);
          this.selectedAccount.set(response.data.account.account)
          return response;
        }),
      );
  }
}

